import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Formik } from 'formik'
import { isEmpty, isNil } from 'lodash'
import classNames from 'classnames'

import { toast } from '@/components/toasts/toast.helpers'
import {
  fetchOrgDefaultBitFlow,
  saveBitFlowForPlaybook,
} from '@/reducers/playbooks/playbook.actions'
import {
  setRTNPrompt,
  setRTNBitFlow,
  setRTNOrg,
  setRTNOrgDefaultFlow,
} from '@/reducers/playbooks/playbook.redux'
import { CHANNEL_TYPE } from '@/utils/constants'
import { PageHeader } from '@/components/headers/PageHeader'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'

import { PromptForm } from './PromptForm'
import { VanityMetrics } from './components/VanityMetrics'
import { OrgDefaultsForm } from './components/OrgDefaultsForm'

import './NotetakerPage.scss'

const NOTETAKER_VIEW = {
  PLAYBOOK: 'playbook',
  ORG_DEFAULTS: 'orgDefaults',
}

const NotetakerPage = () => {
  const dispatch = useDispatch()
  // The save derives edges from `$tokens` only when the feature is on, or it
  // would run invisibly against descriptions nobody was shown the UI for.
  const { notetakerBranchingEnabled: branchingEnabled = false } = useFlags()
  const { rtnPrompt, rtnOrg, rtnBitFlow, rtnOrgDefaultFlow } = useSelector(
    (state) => state.playbook
  )
  const { organizationid } = useSelector((state) => state.currentUser)
  const [selectedChannel, setSelectedChannel] = useState(CHANNEL_TYPE.CALL)
  const [viewMode, setViewMode] = useState(NOTETAKER_VIEW.PLAYBOOK)

  const isAdmin = organizationid === 1
  // Admins manage other orgs, so the org defaults follow their selection in
  // the playbook form; everyone else gets their own org's defaults.
  const effectiveOrgId = isAdmin ? rtnOrg : organizationid
  const hasOrgDefaults = !isNil(rtnOrgDefaultFlow) && !isEmpty(rtnOrgDefaultFlow.fields)

  useEffect(() => {
    dispatch(setRTNPrompt(''))
    dispatch(setRTNBitFlow(null))

    // The admin org selection lives in PlaybookForm local state and resets on
    // every visit — clear its redux mirror on the way out so the next visit
    // doesn't fetch a previously selected org's defaults.
    return () => {
      dispatch(setRTNOrg(null))
      dispatch(setRTNOrgDefaultFlow(null))
    }
  }, [])

  useEffect(() => {
    if (effectiveOrgId) {
      dispatch(fetchOrgDefaultBitFlow(effectiveOrgId))
    }
  }, [effectiveOrgId])

  // The defaults view is unreachable when there are no defaults (the switch
  // hides), so kick back to the playbook view, e.g. when an admin moves to an
  // org without a default flow.
  useEffect(() => {
    if (!hasOrgDefaults && viewMode === NOTETAKER_VIEW.ORG_DEFAULTS) {
      setViewMode(NOTETAKER_VIEW.PLAYBOOK)
    }
  }, [hasOrgDefaults])

  // Notetaker is backed entirely by bit flows now — saving always versions the
  // loaded flow. Save is gated on the same rtnBitFlow that drives showEditor
  // (PromptForm), so this branch is defense-in-depth: it can't be reached from
  // the UI, but guards against ever POSTing a flow-less save (which would mint
  // a brand-new flow). Surface an error rather than do that.
  const saveLoadedBitFlow = (values, { forceNewVersion = false } = {}) => {
    if (!rtnBitFlow) {
      toast.error('Failed to update prompt for playbook')
      return
    }

    dispatch(
      saveBitFlowForPlaybook({
        playbookCid: values.selected_playbook_cid,
        promptFields: values.prompt_fields,
        callType: values.selected_channel,
        organizationId: rtnOrg || organizationid,
        flowUuid: rtnBitFlow.flow_uuid || null,
        llmModelId: values.llm_model_id,
        systemPrompt: values.system_prompt,
        userPrompt: values.user_prompt,
        maxResponseTokens: values.max_response_tokens,
        fieldLinks: values.field_links,
        branchingEnabled,
        forceNewVersion,
      })
    )
  }

  const handleUpdateRTNPrompt = (values) => saveLoadedBitFlow(values)

  // Restoring puts an older version back in service by saving it as a new
  // version — the form already holds that version's fields, so the only
  // difference from a normal save is that they're unchanged, which the save
  // would otherwise treat as nothing to do.
  const handleRestoreVersion = (values) => saveLoadedBitFlow(values, { forceNewVersion: true })

  const promptInitialValues = {
    llm_model_id: rtnPrompt?.llm_model_id || null,
    system_prompt: rtnPrompt?.system_prompt || '',
    user_prompt: rtnPrompt?.user_prompt || '',
    max_response_tokens: rtnPrompt?.max_response_tokens ?? null,
    call_id: '',
    prompt_fields: [{ key: '', value: '' }],
    field_links: [],
    // The loaded configuration as RowEditorForm seeded it — rows, conditions,
    // and the shared prompt that saves with them. The action bar diffs against
    // these and Discard puts them back; see unsavedChanges.helpers.
    loaded_prompt_fields: [],
    loaded_field_links: [],
    loaded_flow_key: null,
    loaded_llm_model_id: rtnPrompt?.llm_model_id || null,
    loaded_system_prompt: rtnPrompt?.system_prompt || '',
    loaded_user_prompt: rtnPrompt?.user_prompt || '',
    loaded_max_response_tokens: rtnPrompt?.max_response_tokens ?? null,
    selected_playbook_cid: '',
    selected_channel: CHANNEL_TYPE.CALL,
  }

  const validationSchema = Yup.object().shape({
    call_id: Yup.number().typeError('Call ID must be a number'),
    prompt_fields: Yup.array().of(
      Yup.object().shape({
        key: Yup.string().required('Key is required'),
        value: Yup.string()
          .max(5000, 'Value cannot be more than 5,000 characters')
          .required('Value is required'),
      })
    ),
  })

  return (
    <>
      <PageHeader data-testid="notetaker-page" title="Notetaker" />
      <VanityMetrics selectedChannel={selectedChannel} />

      {hasOrgDefaults && (
        <SegmentedControl
          className="notetaker-view-switch"
          items={[
            {
              accessor: NOTETAKER_VIEW.PLAYBOOK,
              label: 'Playbook Configuration',
              active: viewMode === NOTETAKER_VIEW.PLAYBOOK,
              onClick: () => setViewMode(NOTETAKER_VIEW.PLAYBOOK),
              dataTestId: 'playbook-view-tab',
            },
            {
              accessor: NOTETAKER_VIEW.ORG_DEFAULTS,
              label: 'Organization Defaults',
              active: viewMode === NOTETAKER_VIEW.ORG_DEFAULTS,
              onClick: () => setViewMode(NOTETAKER_VIEW.ORG_DEFAULTS),
              dataTestId: 'org-defaults-view-tab',
            },
          ]}
        />
      )}

      {/* Both views stay mounted — hiding instead of unmounting preserves
          unsaved edits and the admin's org selection across tab switches. */}
      <div
        className={classNames('notetaker-view', {
          'notetaker-view--hidden': viewMode !== NOTETAKER_VIEW.PLAYBOOK,
        })}
        data-testid="playbook-view"
      >
        <Formik
          initialValues={promptInitialValues}
          onSubmit={handleUpdateRTNPrompt}
          validateOnMount
          validationSchema={validationSchema}
        >
          {({ handleSubmit, values, setFieldValue }) => {
            return (
              <PromptForm
                handleSubmit={handleSubmit}
                values={values}
                setFieldValue={setFieldValue}
                onChannelChange={setSelectedChannel}
                onRestoreVersion={handleRestoreVersion}
                onSave={handleUpdateRTNPrompt}
              />
            )
          }}
        </Formik>
      </div>

      <div
        className={classNames('notetaker-view', {
          'notetaker-view--hidden': viewMode !== NOTETAKER_VIEW.ORG_DEFAULTS,
        })}
        data-testid="org-defaults-view"
      >
        <OrgDefaultsForm />
      </div>
    </>
  )
}

export default NotetakerPage
